/**
 * Invite Accept - Public page for accepting user invitations
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingState from '../components/LoadingState';
import { AlertTriangle } from 'lucide-react';
import apiService from '../services/api';
import styles from './InviteAccept.module.css';

const InviteAccept = () => {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get token from either path parameter or query string
  const token = pathToken || searchParams.get('token');
  
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (token) {
      loadInvitationStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadInvitationStatus = async () => {
    try {
      setLoading(true);
      const status = await apiService.getInvitationStatus(token);
      setInvitationInfo(status);
      
      if (status.is_expired) {
        setError('This invitation has expired. Please contact your administrator for a new invitation.');
      } else if (status.is_used) {
        setError('This invitation has already been used. Please try logging in instead.');
      }
      
    } catch (err) {
      setError('Invalid or expired invitation link.');
      console.error('Failed to load invitation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setAccepting(true);
      setError(null);
      
      await apiService.acceptInvitation(token, formData.password, formData.confirmPassword);
      
      // Success - redirect to auth/login
      navigate('/auth', {
        state: {
          message: 'Your account has been activated! Please log in with your email and new password.'
        }
      });
      
    } catch (err) {
      setError(err.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading invitation..." />;
  }

  if (error && !invitationInfo) {
    return (
      <div className={styles.container}>
        <Card level={1} className={styles.inviteCard}>
          <div className={styles.errorContainer}>
            <AlertTriangle size={48} style={{ color: 'var(--color-error)', marginBottom: 'var(--spacing-md)' }} />
            <h1>Invalid Invitation</h1>
            <p>{error}</p>
            <Button onClick={() => navigate('/auth')} variant="primary">
              Go to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card level={1} className={styles.inviteCard}>
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
          <h1 style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--spacing-md)' }}>Accept Your Invitation</h1>
          {invitationInfo && (
            <div className={styles.invitationInfo}>
              <p>You've been invited to join <strong>{invitationInfo.club_name}</strong> as a <strong>{invitationInfo.role_name}</strong>.</p>
              {invitationInfo.custom_message && (
                <div className={styles.customMessage}>
                  <p><em>{invitationInfo.custom_message}</em></p>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
          </div>
        )}

        {!error && invitationInfo && (
          <form onSubmit={handleSubmit}>
            <div className={styles.formSection}>
              <h2>Set Your Password</h2>
              <p>Please create a secure password for your account.</p>

              <div className={styles.formGroup}>
                <label htmlFor="password">
                  Password <span className={styles.required}>*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={validationErrors.password ? styles.error : ''}
                  placeholder="Enter your password"
                  required
                />
                {validationErrors.password && (
                  <span className={styles.fieldError}>{validationErrors.password}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword">
                  Confirm Password <span className={styles.required}>*</span>
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={validationErrors.confirmPassword ? styles.error : ''}
                  placeholder="Confirm your password"
                  required
                />
                {validationErrors.confirmPassword && (
                  <span className={styles.fieldError}>{validationErrors.confirmPassword}</span>
                )}
              </div>
            </div>

            <div className={styles.formActions}>
              <Button
                type="submit"
                variant="primary"
                disabled={accepting}
              >
                {accepting ? 'Activating Account...' : 'Activate Account'}
              </Button>
            </div>
          </form>
        )}

        <div className={styles.loginLink}>
          <p>Already have an account? <a href="/auth">Sign In</a></p>
        </div>
      </Card>
    </div>
  );
};

export default InviteAccept;